/**
 * BR-12 `/analytics` - campaign performance and spend.
 *
 * The brand's view of the same projections the creator reads, which is
 * deliberate: if a creator is told a post counted 25,450 views and the
 * brand is told something else, one of them is being lied to, and neither
 * would know which.
 *
 * The SRS's "Metric provenance, denominators and data-as-of labels" is
 * three separate refusals, and each one is visible on this screen:
 *
 *   provenance   the figures are provider-reported, and the screen says so
 *                rather than calling them verified views;
 *   denominators effective CPM is shown only where eligible views exist -
 *                a campaign with none shows "-", not 0.00 (TC-047);
 *   data-as-of   the freshness shown is the *oldest* successful collection
 *                across the campaign's posts, because a report is only as
 *                current as its stalest input, and any campaign with a
 *                stale post says how many.
 *
 * Campaigns in different currencies are separate cards with separate
 * totals. There is no place on this screen where two currencies are added.
 */
import { useCallback, useEffect, useState } from "react";
import { Badge, Card, Eyebrow, PageHeader, StatTile, StateBlock, Money } from "@ui/primitives";
import { fontSize, space } from "@design/index";
import { fromMinorString, type CurrencyCode } from "@contracts/money";
import { api } from "@app/shared/api";

interface Report {
  campaign_id: string; campaign_name: string; currency: string; state: string;
  submissions: number; approved_submissions: number; measured_submissions: number;
  eligible_views: string; reported_views: string | null;
  recognized_minor: string; committed_minor: string;
  funded_minor: string; unallocated_minor: string;
  effective_cpm_minor: string | null;
  stale_submissions: number; data_as_of: string | null;
}

interface Submission {
  submission_id: string; platform: string; post_id: string | null;
  submission_state: string; moderation_state: string; currency: string;
  reported_views: string | null; eligible_views: string | null;
  excluded_views: string | null; exclusion_reason: string | null;
  posted_minor: string; cap_applied: boolean;
  effective_cpm_minor: string | null;
  last_outcome: string | null; last_success_at: string | null;
}

const Amount = ({ minor, currency, emphasis = false }: {
  minor: string; currency: string; emphasis?: boolean;
}) => <Money value={fromMinorString(minor, currency as CurrencyCode)} emphasis={emphasis} />;

const Views = ({ value }: { value: string | null }) =>
  value === null || value === undefined
    ? <span style={{ color: "var(--faint)" }} title="Not reported">-</span>
    : <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {Number(value).toLocaleString("en-GB")}
      </span>;

function when(value: string | null) {
  if (!value) return "never";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "never" : d.toISOString().slice(0, 16).replace("T", " ");
}

export function AnalyticsScreen() {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<Submission[] | null>(null);

  useEffect(() => {
    void api.get<{ reports: Report[] }>("/analytics").then((r) => {
      if (r.ok) setReports(r.data.reports); else setError(r.message);
    });
  }, []);

  const openCampaign = useCallback(async (id: string) => {
    setOpen(id); setDetail(null);
    const r = await api.get<{ submissions: Submission[] }>(`/analytics/${id}`);
    if (r.ok) setDetail(r.data.submissions);
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Analytics"
        title="Campaign performance"
        description="What your campaigns were reported to have earned in views, what counted, and what you have been charged for it."
      />

      {error ? <StateBlock kind="error" title="Could not load analytics" detail={error} />
        : reports === null ? <StateBlock kind="loading" title="Loading" />
        : reports.length === 0 ? (
          <StateBlock kind="empty" title="No campaigns yet"
            detail="Once a campaign is funded and creators start posting, its performance appears here." />
        ) : (
          <div style={{ display: "grid", gap: space(4) }}>
            {reports.map((r) => (
              <Card key={r.campaign_id} padding={18} style={{ display: "grid", gap: space(3) }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "baseline", gap: space(3), flexWrap: "wrap" }}>
                  <Eyebrow>{r.campaign_name} · {r.currency}</Eyebrow>
                  <div style={{ display: "flex", gap: space(2), flexWrap: "wrap" }}>
                    <Badge tone={r.state === "ACTIVE" ? "ok" : "neutral"}>{r.state}</Badge>
                    {r.stale_submissions > 0 ? (
                      <Badge tone="warn">
                        {r.stale_submissions} post{r.stale_submissions === 1 ? "" : "s"} stale
                      </Badge>
                    ) : null}
                  </div>
                </div>

                <div style={{ display: "grid", gap: space(2),
                              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
                  <StatTile label="Counted views" tone="ok"
                    value={<Views value={r.eligible_views} />}
                    hint="What you were charged for" />
                  <StatTile label="Reported views"
                    value={<Views value={r.reported_views} />}
                    hint={r.reported_views === null
                      ? "Nothing has been collected yet"
                      : "Provider-reported, not unique people"} />
                  <StatTile label="Spent"
                    value={<Amount minor={r.recognized_minor} currency={r.currency} emphasis />}
                    hint="Recognized against creators' earnings" />
                  <StatTile label="Held for creators" tone="process"
                    value={<Amount minor={r.committed_minor} currency={r.currency} />}
                    hint="Reserved for creators who have joined" />
                  <StatTile label="Remaining"
                    value={<Amount minor={r.unallocated_minor} currency={r.currency} />}
                    hint="Funded and still available for new creators" />
                  <StatTile label="Effective CPM"
                    value={r.effective_cpm_minor === null
                      ? <span style={{ color: "var(--faint)" }}>-</span>
                      : <Amount minor={String(Math.round(Number(r.effective_cpm_minor)))}
                                currency={r.currency} />}
                    hint={r.effective_cpm_minor === null
                      ? "No counted views, so there is no rate"
                      : "Spent per 1,000 counted views"} />
                </div>

                <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)",
                            lineHeight: 1.6 }}>
                  {r.submissions} submission{r.submissions === 1 ? "" : "s"} ·
                  {" "}{r.approved_submissions} approved ·
                  {" "}{r.measured_submissions} measured ·
                  {" "}{r.data_as_of === null
                    ? "nothing has been collected yet"
                    : `data as of ${when(r.data_as_of)} - the oldest successful `
                      + "collection across this campaign's posts"}
                </p>

                <div>
                  <button type="button" onClick={() => void openCampaign(r.campaign_id)}
                    style={{ background: "none", border: 0, padding: 0, cursor: "pointer",
                             color: "var(--link)", font: "inherit",
                             fontSize: fontSize.label }}>
                    {open === r.campaign_id ? "Showing each post" : "Show each post"}
                  </button>
                </div>

                {open === r.campaign_id ? (
                  detail === null ? <StateBlock kind="loading" title="Loading" /> : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ borderCollapse: "collapse", width: "100%",
                                      fontSize: fontSize.label }}>
                        <thead>
                          <tr>
                            {["Platform", "Reported", "Counted", "Not counted", "Charged",
                              "Measured"].map((h, i) => (
                              <th key={h} style={{
                                textAlign: i === 0 || i === 5 ? "left" : "right",
                                padding: "8px 12px 8px 0",
                                borderBottom: "1px solid var(--line)",
                                color: "var(--muted)", fontWeight: 600, whiteSpace: "nowrap",
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detail.map((s) => (
                            <tr key={s.submission_id}>
                              <td style={cell()}>
                                {s.platform}
                                {s.cap_applied
                                  ? <div style={{ color: "var(--muted)",
                                                  fontSize: fontSize.micro }}>cap reached</div>
                                  : null}
                              </td>
                              <td style={cell(true)}><Views value={s.reported_views} /></td>
                              <td style={cell(true)}><Views value={s.eligible_views} /></td>
                              <td style={cell(true)}>
                                <Views value={s.excluded_views} />
                                {s.exclusion_reason ? (
                                  <div style={{ color: "var(--muted)",
                                                fontSize: fontSize.micro, textAlign: "right" }}>
                                    {s.exclusion_reason}
                                  </div>
                                ) : null}
                              </td>
                              <td style={cell(true)}>
                                <Amount minor={s.posted_minor} currency={s.currency} /></td>
                              <td style={cell()}>
                                {when(s.last_success_at)}
                                {s.last_outcome && s.last_outcome !== "OK" ? (
                                  <div style={{ color: "var(--warn-fg)",
                                                fontSize: fontSize.micro }}>
                                    {s.last_outcome.toLowerCase().replaceAll("_", " ")}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : null}
              </Card>
            ))}

            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)",
                        lineHeight: 1.6 }}>
              View counts are what each platform reported through the measurement route.
              They are not a count of unique people, and they are not evidence of
              geography, sales or organic-only traffic. Each campaign is reported in its
              own currency and the figures are never added across them.
            </p>
          </div>
        )}
    </>
  );
}

const cell = (numeric = false) => ({
  textAlign: numeric ? "right" as const : "left" as const,
  padding: "10px 12px 10px 0",
  borderBottom: "1px solid var(--line-soft)",
  verticalAlign: "top" as const,
});
